import React, { Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "./components/theme-provider";
import { CartProvider } from "@/contexts/CartContext";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as SonnerToaster } from "sonner";
import { Toaster as RadixToaster } from "@/components/ui/toaster";
import { AgentExecutionProvider } from "@/contexts/AgentExecutionContext";
import { DocumentViewerProvider } from "@/contexts/DocumentViewerContext";
import PrivateRoute from "./components/PrivateRoute";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import { useAuth } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import CreateTenant from "./pages/CreateTenant";
import { AuthDebug } from "./components/AuthDebug";
import { AuthFlowTest } from "./components/AuthFlowTest";
import { SessionExpirationTest } from "./components/SessionExpirationTest";
import { IdleDetectionTest } from "./components/IdleDetectionTest";

// Page/Component imports
import Home from "./components/Home";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import Notifications from "./components/dashboard/Notifications";
import Calendar from "./components/dashboard/Calendar";
import Settings from "./components/dashboard/Settings";
import Profile from "./components/dashboard/Profile";
import AgentDashboard from "./components/dashboard/AgentDashboard";
import SubscriptionGuard from "./components/SubscriptionGuard";
import CartPage from "@/pages/cart";
import PlanesPage from "@/pages/planes";
import PricingPage from "@/pages/PricingPage";
import Blog from "@/pages/Blog";
import BlogList from "@/components/blog/BlogList";
import BlogArticle from "@/components/blog/BlogArticle";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiesPolicy from "@/pages/CookiesPolicy";
import Disclaimer from "@/pages/Disclaimer";
import NotFound from "@/pages/NotFound";
import CertifiedEmailView from "@/pages/CertifiedEmailView";
import ChatLegalDashboard from "./pages/dashboard/legal-chatbot";
import CampusConsultorioDashboard from "./pages/dashboard/campus-consultorio";
import CampusDashboard from "./pages/dashboard/campus";
import ConsultorioDashboard from "./pages/dashboard/consultorio";
// import LegalChatbotV2 from "./pages/dashboard/legal-chatbot-v2";
import LegalResearchDashboard from "./pages/dashboard/legal-research";
import DocumentGeneratorDashboard from "./pages/dashboard/document-generator";
import DocumentDraftingV2 from "./pages/dashboard/document-drafting-v2";
import DocumentHistory from "./pages/dashboard/document-history";
import LegalEditorPage from "@/pages/dashboard/legal-editor";
import DocumentsList from "./pages/dashboard/documents-list";
import ContractReviewDashboard from "./pages/dashboard/contract-review";
import LegalDiagnosisDashboard from "./pages/dashboard/case-prediction";
import PatentAnalysisDashboard from "./pages/dashboard/patent-search";
import PatentSearchDashboard from "./pages/dashboard/patent-search";
import LegalNotificationsDashboard from "./pages/dashboard/legal-notifications";
import ComplianceMonitoringDashboard from "./pages/dashboard/compliance-monitoring";
import ConsumerAssistantDashboard from "./pages/dashboard/consumer-assistant";
import ContractReviewV2 from "./pages/dashboard/contract-review-v2";
import LegalResearchV2 from "./pages/dashboard/legal-research-v2";
import CasePredictionV2 from "./pages/dashboard/case-prediction-v2";
import SpecializedAgentsV2 from "./pages/dashboard/specialized-agents-v2";
const DocumentWizard = React.lazy(() => import("./pages/dashboard/DocumentWizard"));
import AboutUs from "@/pages/AboutUs";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import EmailConfirmation from "./pages/EmailConfirmation";
import ComplianceMonitoringV2 from "./pages/dashboard/compliance-monitoring-v2";
import PatentSearchV2 from "./pages/dashboard/patent-search-v2";
import WhistleblowerV2 from "./pages/dashboard/whistleblower-v2";
import CasePredictionDashboard from "./pages/dashboard/case-prediction";
import CasePredictionResultPage from "./pages/dashboard/case-prediction-result";
import { CasePredictionReport } from "./pages/agents/CasePredictionReport";
import ContractAnalysisDashboard from "./pages/dashboard/contract-review";
import AIAgentsSection from "./components/dashboard/AIAgentsSection";
import MobileAgentsDirectory from "./pages/dashboard/mobile-agents-directory";
import MobileAgentDetailPage from "./pages/dashboard/mobile-agent-detail";
import MobileAgentRunPage from "./pages/dashboard/mobile-agent-run";
import MobileAgentHistoryPage from "./pages/dashboard/mobile-agent-history";
import {
  MobileAgentDetailRedirect,
  MobileAgentRouteRedirect,
  MobileAgentRunRedirect,
} from "./components/dashboard/mobile/MobileAgentRouteRedirect";
import FriendInvite from "./pages/FriendInvite";
import { FriendInviteManager } from "./components/dashboard/FriendInviteManager";
import EmailValidation from "./pages/EmailValidation";
import RequestDemo from "./pages/RequestDemo";
import CalculadoraAhorroLanding from "./pages/CalculadoraAhorroLanding";
import PublicCalculadoras from "./pages/PublicCalculadoras";
import CarteraPHLanding from "./pages/CarteraPHLanding";
import CarteraPHBrochure from "./pages/CarteraPHBrochure";
import CarteraPHDemoPage from "./pages/CarteraPHDemoPage";
import ControlExpedientesLanding from "./pages/ControlExpedientesLanding";
const WarRoomCourtesyPage = React.lazy(() => import("./pages/WarRoomCourtesyPage"));
import FloatingWhatsApp from "./components/FloatingWhatsApp";

import DespachosLayout from "./pages/dashboard/despachos/DespachosLayout";
import DespachosDashboard from "./pages/dashboard/despachos/DespachosDashboard";
import {
  ClientesPage,
  ProcesosPage,
  TramitesPage,
  CalendarioPage,
  ListaTareasPage,
  ContactosPage,
  EnviarEmailPage,
  ArchivosDespachoPage,
  MisArchivosPage,
  DescargarTirantBoxPage,
  ListadoRecibosPage,
  CrearReciboManualPage,
  GraficosPage,
  LepdPage,
  MisRecursosSharedPage,
  AllRecursosSharedPage,
  ConfiguracionPage,
  SamaiPage,
} from "./pages/dashboard/despachos/pages/DespachosPages";
import { ClientDetailPage } from "./pages/dashboard/despachos/pages/ClientDetailPage";
import { ProcessDetailPage } from "./pages/dashboard/despachos/pages/ProcessDetailPage";
import { JudicialSearchPage } from "./pages/dashboard/despachos/pages/JudicialSearchPage";
import Integrations from "./pages/dashboard/integrations";
import ROIDashboard from "./pages/dashboard/roi-dashboard";
import ControlCenter from "./pages/dashboard/ControlCenter";
import IntegrationDocs from "./pages/docs/IntegrationDocs";
import Maintenance from "./pages/Maintenance";
import SkillsPage from "./pages/dashboard/skills/Skills";
import PlaybookCreationPage from "./pages/dashboard/skills/PlaybookCreation";
import EditSkillPage from "./pages/dashboard/skills/EditSkill";
import AIPlaybookGeneratorPage from "./pages/dashboard/skills/AIPlaybookGenerator";
import AdminOnlineUsers from "./pages/dashboard/AdminOnlineUsers";
import AdminPlans from "./pages/dashboard/AdminPlans";
import AdminIngestRegistry from "./pages/dashboard/AdminIngestRegistry";
import AdminBanners from "./pages/dashboard/AdminBanners";
import SubscriptionUsage from "./pages/dashboard/subscription-usage";
import Boletines from "./pages/dashboard/boletines/Boletines";
import PublicBulletinPage from "./pages/PublicBulletinPage";
import PublicSharedDocument from "./pages/PublicSharedDocument";
import PublicDebtorPortalPage from "./pages/public/PublicDebtorPortalPage";
import CalculadorasHub from "./pages/dashboard/calculadoras/CalculadorasHub";
const ContractReviewLanding = lazy(() => import("./pages/features/contract-review-landing"));
const DocumentDraftingLanding = lazy(() => import("./pages/features/document-drafting-landing"));
const LegalResearchLanding = lazy(() => import("./pages/features/legal-research-landing"));
const SocialAssets = lazy(() => import("./pages/features/social-assets"));
import PaymentResult from "./pages/payments/PaymentResult";

const MAINTENANCE_MODE = false; // Turned off as per user request for "banner only"

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Wrapper component for SessionExpiredModal that uses auth context
function SessionExpiredModalWrapper() {
  const { showSessionExpiredModal, setShowSessionExpiredModal } = useAuth();

  return (
    <SessionExpiredModal
      isOpen={showSessionExpiredModal}
      onClose={() => setShowSessionExpiredModal(false)}
    />
  );
}

function AppRoutes() {
  const { user } = useAuth();

  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-slate-900">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
        </div>
      }
    >
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Index />} />
        <Route path="/home" element={<Index />} />
        <Route path="/docs/integration" element={<IntegrationDocs />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/features/contract-review" element={<ContractReviewLanding />} />
        <Route path="/features/document-drafting" element={<DocumentDraftingLanding />} />
        <Route path="/features/legal-research" element={<LegalResearchLanding />} />
        <Route path="/features/contract-review/assets" element={<SocialAssets />} />
        <Route path="/request-demo" element={<RequestDemo />} />
        <Route path="/cartera-ph" element={<CarteraPHLanding />} />
        <Route path="/recuperacion-cartera-ph" element={<CarteraPHLanding />} />
        <Route path="/propiedad-horizontal" element={<CarteraPHLanding />} />
        <Route path="/brochure-cartera-ph" element={<CarteraPHBrochure />} />
        <Route path="/cartera-ph/brochure" element={<CarteraPHBrochure />} />
        <Route path="/folleto-cartera-ph" element={<CarteraPHBrochure />} />
        <Route path="/cartera-ph/demo" element={<CarteraPHDemoPage />} />
        <Route path="/demo-cartera-ph" element={<CarteraPHDemoPage />} />
        <Route path="/calculadora" element={<CalculadoraAhorroLanding />} />
        <Route path="/calculadora-ahorro" element={<CalculadoraAhorroLanding />} />
        <Route path="/ahorro" element={<CalculadoraAhorroLanding />} />
        <Route path="/control-expedientes" element={<ControlExpedientesLanding />} />
        <Route path="/expedientes" element={<ControlExpedientesLanding />} />
        <Route path="/seguimiento-procesos" element={<ControlExpedientesLanding />} />
        <Route path="/war-room" element={<WarRoomCourtesyPage />} />
        <Route path="/warroom" element={<WarRoomCourtesyPage />} />
        <Route path="/cortesia/war-room" element={<WarRoomCourtesyPage />} />
        <Route path="/legal-academy" element={<WarRoomCourtesyPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/precios" element={<PricingPage />} />
        <Route path="/calculadoras" element={<PublicCalculadoras />} />
        <Route path="/calculadora-perjuicios" element={<PublicCalculadoras initialCalc="perjuicios-traslado-pensional" />} />
        <Route path="/calculadora-traslado-pensional" element={<PublicCalculadoras initialCalc="perjuicios-traslado-pensional" />} />
        <Route path="/email-confirmation" element={<EmailConfirmation />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/planes" element={<PricingPage />} />
        <Route path="/payment/result" element={<PaymentResult />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:articleId" element={<BlogArticle />} />
        <Route path="/boletin/:slug" element={<PublicBulletinPage />} />
        <Route path="/novedades/:slug" element={<PublicBulletinPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiesPolicy />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/correo-certificado/:emailId" element={<CertifiedEmailView />} />
        <Route path="/d/:shareToken" element={<PublicSharedDocument />} />
        {/* Portal Público de Autogestión y Acuerdos de Pago del Deudor (PH) */}
        <Route path="/portal-deudor" element={<PublicDebtorPortalPage />} />
        <Route path="/portal-deudor/:conjuntoSlug/:token" element={<PublicDebtorPortalPage />} />
        <Route path="/portal-deudor/:token" element={<PublicDebtorPortalPage />} />
        <Route path="/acuerdo" element={<PublicDebtorPortalPage />} />
        <Route path="/acuerdo/:conjuntoSlug/:token" element={<PublicDebtorPortalPage />} />
        <Route path="/acuerdo/:token" element={<PublicDebtorPortalPage />} />
        <Route path="/friend-invite/:inviteCode" element={<FriendInvite />} />
        <Route path="/friend-invite" element={<FriendInvite />} />
        <Route path="/debug" element={<AuthDebug />} />
        <Route path="/auth-flow-test" element={<AuthFlowTest />} />
        <Route
          path="/session-expiration-test"
          element={<SessionExpirationTest />}
        />
        <Route path="/email-validation" element={<EmailValidation />} />
        <Route path="/idle-detection-test" element={<IdleDetectionTest />} />
        {/* Tenant creation route */}
        <Route
          path="/create-tenant"
          element={
            <PrivateRoute>
              <CreateTenant />
            </PrivateRoute>
          }
        />
        {/* Protected routes */}
        <Route
          path="/cart"
          element={
            <PrivateRoute>
              <CartPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }>
          <Route index element={<Dashboard />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="settings" element={<Settings />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="agents"
            element={
              <MobileAgentRouteRedirect to="/dashboard/agents/mobile">
                <AIAgentsSection />
              </MobileAgentRouteRedirect>
            }
          />
          <Route path="agents/mobile" element={<MobileAgentsDirectory />} />
          <Route path="agents/mobile/:agentSlug" element={<MobileAgentDetailPage />} />
          <Route path="agents/mobile/:agentSlug/run" element={<MobileAgentRunPage />} />
          <Route path="agents/mobile/:agentSlug/history" element={<MobileAgentHistoryPage />} />
          <Route path="wizard" element={
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-slate-900"><Loader2 className="h-10 w-10 animate-spin text-indigo-500" /></div>}>
              <DocumentWizard />
            </Suspense>
          } />

          {/* Agent routes with subscription guard */}
          <Route element={<SubscriptionGuard />}>
            <Route
              path="agent/:agentSlug"
              element={
                <MobileAgentDetailRedirect>
                  <AgentDashboard />
                </MobileAgentDetailRedirect>
              }
            />
          </Route>
          <Route
            path="campus"
            element={<CampusDashboard />}
          />
          <Route
            path="consultorio"
            element={<ConsultorioDashboard />}
          />
          <Route
            path="agents/campus"
            element={<CampusDashboard />}
          />
          <Route
            path="agents/consultorio"
            element={<ConsultorioDashboard />}
          />
          <Route
            path="campus-consultorio"
            element={<CampusConsultorioDashboard />}
          />
          <Route
            path="agents/campus-consultorio"
            element={<CampusConsultorioDashboard />}
          />
          <Route
            path="agents/legal-chatbot"
            element={<ChatLegalDashboard />}
          />
          {/* <Route
            path="agents/legal-chatbot-v2"
            element={<LegalChatbotV2 />}
          /> */}
          <Route
            path="agents/legal-notifications"
            element={<LegalNotificationsDashboard />}
          />
          <Route
            path="legal-notifications"
            element={<LegalNotificationsDashboard />}
          />
          <Route
            path="cartera-ph"
            element={<LegalNotificationsDashboard />}
          />
          <Route
            path="agents/document-generator"
            element={<DocumentGeneratorDashboard />}
          />
          <Route
            path="agents/document-drafting-v2"
            element={<DocumentDraftingV2 />}
          />
          <Route
            path="agents/document-history/:documentId"
            element={<DocumentHistory />}
          />
          <Route
            path="collab"
            element={<Navigate to="/dashboard/documents" replace />}
          />
          <Route
            path="collab/:documentId"
            element={<LegalEditorPage />}
          />
          <Route
            path="documents"
            element={<DocumentsList />}
          />
          <Route
            path="agents/contract-review"
            element={<ContractAnalysisDashboard />}
          />
          <Route
            path="agents/contract-review-v2"
            element={<ContractReviewV2 />}
          />
          <Route
            path="agents/legal-research"
            element={<LegalResearchDashboard />}
          />
          <Route
            path="agents/legal-research-v2"
            element={<LegalResearchV2 />}
          />
          <Route
            path="agents/specialized"
            element={
              <MobileAgentRouteRedirect to="/dashboard/agents/mobile/specialized">
                <SpecializedAgentsV2 />
              </MobileAgentRouteRedirect>
            }
          />
          <Route
            path="agents/legal-diagnosis"
            element={<LegalDiagnosisDashboard />}
          />
          <Route
            path="agents/patent-analysis"
            element={<PatentSearchV2 />}
          />
          <Route
            path="agents/patent-search"
            element={<PatentSearchV2 />}
          />
          <Route
            path="agents/debt-collection"
            element={<LegalNotificationsDashboard />}
          />
          <Route
            path="agents/deb-collection"
            element={<LegalNotificationsDashboard />}
          />
          <Route
            path="agents/compliance-monitoring"
            element={<ComplianceMonitoringV2 />}
          />
          <Route
            path="agents/whistleblower"
            element={<WhistleblowerV2 />}
          />
          <Route
            path="agents/ip-assistant"
            element={<PatentSearchV2 />}
          />
          <Route
            path="agents/litigation-predictor"
            element={<CasePredictionDashboard />}
          />
          <Route
            path="agents/case-prediction-v2"
            element={<CasePredictionV2 />}
          />
          <Route
            path="agents/litigation-predictor/result"
            element={<CasePredictionResultPage />}
          />
          <Route
            path="integrations"
            element={<Integrations />}
          />
          <Route
            path="roi"
            element={<ROIDashboard />}
          />
          <Route
            path="control-center"
            element={<ControlCenter />}
          />
          <Route path="skills" element={<SkillsPage />} />
          <Route path="skills/playbook-creation" element={<PlaybookCreationPage />} />
          <Route path="skills/edit/:skillId" element={<EditSkillPage />} />
          <Route path="skills/generate" element={<AIPlaybookGeneratorPage />} />
          <Route path="admin/online-users" element={<AdminOnlineUsers />} />
          <Route path="admin/plans" element={<AdminPlans />} />
          <Route path="admin/ingest-registry" element={<AdminIngestRegistry />} />
          <Route path="admin/banners" element={<AdminBanners />} />
          <Route path="subscription-usage" element={<SubscriptionUsage />} />
          <Route path="boletines" element={<Boletines />} />
          <Route path="boletines/:slug" element={<Boletines />} />
          <Route path="calculadoras" element={<CalculadorasHub />} />
          {/* Despachos Module Routes */}
          <Route path="despachos" element={<DespachosLayout />}>
            <Route index element={<DespachosDashboard />} />
            <Route path="calculadoras" element={<CalculadorasHub />} />
            <Route path="clientes" element={<ClientesPage />} />
            <Route path="clientes/:id" element={<ClientDetailPage />} />
            <Route path="procesos" element={<ProcesosPage />} />
            <Route path="procesos/:id" element={<ProcessDetailPage />} />
            <Route path="tramites" element={<TramitesPage />} />
            <Route path="busqueda-judicial" element={<JudicialSearchPage />} />
            <Route path="busqueda-judicial/samai" element={<SamaiPage />} />
            <Route path="calendario" element={<CalendarioPage />} />
            <Route path="tareas" element={<ListaTareasPage />} />
            <Route path="contactos" element={<ContactosPage />} />
            <Route path="enviar-email" element={<EnviarEmailPage />} />
            <Route path="archivos" element={<ArchivosDespachoPage />} />
            <Route path="mis-archivos" element={<MisArchivosPage />} />
            <Route path="descargar-tirantbox" element={<DescargarTirantBoxPage />} />
            <Route path="recibos" element={<ListadoRecibosPage />} />
            <Route path="crear-recibo" element={<CrearReciboManualPage />} />
            <Route path="graficos/*" element={<GraficosPage />} />
            <Route path="lepd" element={<LepdPage />} />
            <Route path="mis-recursos" element={<MisRecursosSharedPage />} />
            <Route path="todos-recursos" element={<AllRecursosSharedPage />} />
            <Route path="configuracion" element={<ConfiguracionPage />} />
            <Route path="*" element={<DespachosDashboard />} />
          </Route>
        </Route>

        <Route path="/about-us" element={<AboutUs />} />
        {/* Catch-all route for 404 errors */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <FloatingWhatsApp />
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <AuthProvider>
            <CartProvider>
              <AgentExecutionProvider>
                <DocumentViewerProvider>
                  <TooltipProvider>
                    <Router>
                      <AppRoutes />
                      <SessionExpiredModalWrapper />
                    </Router>
                  </TooltipProvider>
                </DocumentViewerProvider>
              </AgentExecutionProvider>
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
      <SonnerToaster position="top-right" />
      <RadixToaster />
    </QueryClientProvider>
  );
}

export default App;
